import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames';
import { defineMessages, FormattedMessage } from 'react-intl';
import { PortalContent } from 'components/Portal/PortalContent';
import { CallToAction, ACTIONS } from 'components/CallToAction';
import { CmsCta, CMS_CTA_TYPES } from 'components/CmsPage/modules/CmsCta';
import { LeadGenFormContainer } from 'features/LeadGenForm/LeadGenFormContainer';
import { FriendbuyWidgetContainer } from 'components/Friendbuy/FriendbuyWidgetContainer';
import { CTA_COLOR_BY_NAME } from 'constants/colors';
import { ga } from 'helpers/multitrack';
import { renderMarkdown } from 'helpers/markdown';
import { segment } from 'helpers/segment';
import { isEmpty } from 'helpers/verify';
import { BackgroundImage } from 'components/Image/BackgroundImage';

const messages = defineMessages({
  navAction: {
    id: 'hero.nav.action',
    defaultMessage: 'Get ClassPass',
  },
  waitlistAction: {
    id: 'hero.waitlist.action',
    defaultMessage: 'Join the waitlist',
  },
});

/* eslint-disable react/no-array-index-key */
const wrapLines = (lines, className) =>
  (lines || []).map((line, idx) => (
    <span className={className} key={`line-${idx}`}>
      {line}
    </span>
  ));
/* eslint-enable react/no-array-index-key */

export class HeroV2 extends Component {
  constructor(props) {
    super(props);
    this.state = {
      displayFriendbuyWidget: false,
    };
    this.handleCtaClick = this.handleCtaClick.bind(this);
  }

  componentDidMount() {
    const { promoAlias } = this.props;
    if (promoAlias) {
      this.props.setPagePromotion(promoAlias);
    }
  }

  componentWillUnmount() {
    this.props.useSellHeader(false);
  }

  handleFriendbuyWidgetClose = () => this.setState({ displayFriendbuyWidget: false });

  // Logic specific to the hero on CTA click events
  handleCtaClick(cta = {}, isPrimary = false) {
    const { ctaClick: localCtaClick, isWelcomeHero } = this.props;

    if (isWelcomeHero && cta.type === CMS_CTA_TYPES.URL) {
      segment.track('Refer-a-Friend', {
        action: 'raf-hero-cta-click',
      });
      ga('Refer-a-Friend', 'raf-hero-cta-click');
      this.setState({ displayFriendbuyWidget: true });
    } else if (cta.type === CMS_CTA_TYPES.URL || cta.type === CMS_CTA_TYPES.OFFER_CHECKOUT) {
      segment.track('Hero Module', {
        action: 'Clicked CTA',
        label: isPrimary ? 'Primary CTA' : 'Secondary CTA',
        value: 1,
      });
      ga('Hero Module', 'Clicked CTA', isPrimary ? 'Primary CTA' : 'Secondary CTA', 1);
    }

    localCtaClick();
  }

  renderCtaSection() {
    const { ctas } = this.props;

    const renderedCtas = ctas
      .map((cta, idx) => {
        const renderedCta = this.renderCta(cta, idx === 0);
        return (
          renderedCta && (
            <span className="hero__cta" key={`cta-${cta.copy}`}>
              {renderedCta}
            </span>
          )
        );
      })
      .filter(Boolean);

    return !isEmpty(renderedCtas) && <div className="hero__main-cta">{renderedCtas}</div>;
  }

  renderCta(cta, isPrimary) {
    if (!cta.copy) {
      return null;
    }

    const { interpolate } = this.props;

    const extraClass = classNames('bt--lg', 'hero__cta__button', {
      'hero__cta__button--video': cta.type === CMS_CTA_TYPES.YOUTUBE_VIDEO,
      [`hero__cta__button--${cta.color}`]:
        cta.type === CMS_CTA_TYPES.URL && cta.color && !(cta.color in CTA_COLOR_BY_NAME),
    });

    return (
      <CmsCta
        {...cta}
        copy={interpolate(cta.copy)}
        extraClass={extraClass}
        isPrimary={isPrimary}
        onClick={() => this.handleCtaClick(cta, isPrimary)}
      />
    );
  }

  renderLeadCapture() {
    return (
      <div className="hero__lead-gen">
        <FormattedMessage {...messages.waitlistAction}>
          {copy => <LeadGenFormContainer ctaCopy={copy} />}
        </FormattedMessage>
      </div>
    );
  }

  renderHeadingLines(lines, type) {
    const { cmsModuleId, interpolate } = this.props;
    return wrapLines(
      lines.map(interpolate).map((line, index) => renderMarkdown(line, `hero_${cmsModuleId}_${type}_${index}`)),
      `hero__layout__content__${type}__line`
    );
  }

  render() {
    const {
      horizontalAlignment,
      desktopImageUrl,
      headerCtaCopy,
      heroTextColor,
      hideHeaderCta,
      isMobile,
      isLeadCapture,
      legal,
      mobileImageUrl,
      promoAlias,
      subtitle,
      textJustification = 'left',
      title,
      titleSize,
      verticalAlignment = 'middle',
    } = this.props;

    const { displayFriendbuyWidget } = this.state;

    return (
      <div
        className={classNames({
          [`hero hero--website-2-0 hero--${heroTextColor}`]: true,
        })}>
        {displayFriendbuyWidget && <FriendbuyWidgetContainer onClose={this.handleFriendbuyWidgetClose} />}

        {!hideHeaderCta && (
          <PortalContent id="nav-cta">
            <CallToAction
              action={ACTIONS.LINK}
              extraClass="bt--primary bt--sm bt--text-sm header__bt"
              onClick={this.handleCtaClick}
              to={promoAlias ? `/pricing/${promoAlias}` : '/pricing'}>
              {headerCtaCopy || <FormattedMessage {...messages.navAction} />}
            </CallToAction>
          </PortalContent>
        )}
        <BackgroundImage
          disableDeferred
          extraBackgroundClass={classNames('hero__bg', { [`hero__bg--${verticalAlignment}`]: isMobile })}
          url={isMobile ? mobileImageUrl : desktopImageUrl}>
          <div
            className={classNames(
              'hero__layout',
              `hero__layout--${horizontalAlignment}`,
              `hero__layout--${verticalAlignment}`,
              `hero__layout--${textJustification}-justify`
            )}>
            <div className="hero__layout__content">
              <h1 className={classNames('hero__layout__content__title', `hero__layout__content__title--${titleSize}`)}>
                {this.renderHeadingLines(title, 'title')}
              </h1>
              {!isEmpty(subtitle) && (
                <h2 className="hero__layout__content__subtitle">{this.renderHeadingLines(subtitle, 'subtitle')}</h2>
              )}
              {isLeadCapture ? this.renderLeadCapture() : this.renderCtaSection()}
              {// eslint-disable-next-line react/no-danger
              legal && <p className="hero__terms" dangerouslySetInnerHTML={{ __html: legal }} />}
            </div>
          </div>
        </BackgroundImage>
      </div>
    );
  }
}

HeroV2.propTypes = {
  horizontalAlignment: PropTypes.oneOf(['left', 'center', 'right']).isRequired,
  cmsModuleId: PropTypes.string,
  ctaClick: PropTypes.func.isRequired,
  ctas: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        color: PropTypes.string,
        copy: PropTypes.string,
        url: PropTypes.string,
      }),
      PropTypes.shape({
        color: PropTypes.string,
        copy: PropTypes.string,
        theme: PropTypes.string,
        videoId: PropTypes.string,
      }),
    ])
  ).isRequired,
  desktopImageUrl: PropTypes.string,
  headerCtaCopy: PropTypes.string,
  heroTextColor: PropTypes.oneOf(['light', 'dark']).isRequired,
  hideHeaderCta: PropTypes.bool,
  interpolate: PropTypes.func.isRequired,
  isLeadCapture: PropTypes.bool,
  isMobile: PropTypes.bool,
  isWelcomeHero: PropTypes.bool,
  legal: PropTypes.string,
  mobileImageUrl: PropTypes.string,
  promoAlias: PropTypes.string,
  setPagePromotion: PropTypes.func.isRequired,
  subtitle: PropTypes.arrayOf(PropTypes.string),
  textJustification: PropTypes.oneOf(['left', 'far-left', 'center', 'right']),
  title: PropTypes.arrayOf(PropTypes.string).isRequired,
  titleSize: PropTypes.oneOf(['display', 'display-small']),
  useSellHeader: PropTypes.func.isRequired,
  verticalAlignment: PropTypes.oneOf(['top', 'top-split', 'middle', 'middle-split', 'bottom', 'bottom-split']),
};
